import { formatDate } from "$root/localization/formatters";
import { useAgGridRowListener } from "$root/third-party-integrations/ag-grid/ag-grid";
import { DatePickerInput } from "@mdotm/shadcn/mdotm/components";
import type { ColDef } from "ag-grid-community";
import type { CustomCellEditorProps, CustomCellRendererProps } from "ag-grid-react";
import { useMemo, useRef, useState } from "react";

export function dateColDef<T>(): ColDef<T, string | undefined> {
	return {
		cellEditor: DateCellEditor,
		cellEditorPopup: false,
		cellEditorPopupPosition: undefined,
		cellRenderer: CellRender,
		suppressKeyboardEvent: (params: { editing: boolean }) => params.editing,
	};
}

function DateCellEditor<T>(row: CustomCellEditorProps<T, string | undefined>) {
	const [value, _setValue] = useState<string | null>(row.value ?? null);
	const valueRef = useRef(value);
	const setValue = (val: string | null) => {
		valueRef.current = val;
		_setValue(val);
	};

	useAgGridRowListener(row, "cellEditingStopped", () => {
		row.node.setDataValue(row.column.getColId(), valueRef.current ?? undefined);
	});

	const portalTarget = useMemo(
		() => (row.eGridCell?.closest(".ag-body-viewport") as HTMLElement | null) ?? undefined,
		[row.eGridCell],
	);

	return (
		<DatePickerInput
			portalTarget={portalTarget}
			value={value}
			onChange={setValue}
			className="[&>input]:!border-none [&>input]:!h-full [&>input]:!rounded-none h-full"
			enableClear={value != null}
			onKeyDown={(e) => {
				if (e.key === "Escape" || e.key === "Enter") {
					e.preventDefault();
					e.stopPropagation();
					row.stopEditing();
				}
			}}
		/>
	);
}

function CellRender<T>(row: CustomCellRendererProps<T, string | undefined>) {
	return row.value == null ? "" : formatDate(row.value);
}
